// Smart Sash Admin Panel - Notification Toast Component
// Toast notification display for real-time notifications

import React, { useEffect } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationToastProps {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  onDismiss: (id: string) => void;
}

const iconMap: Record<NotificationType, string> = {
  success: 'fas fa-check-circle',
  error: 'fas fa-exclamation-circle',
  warning: 'fas fa-exclamation-triangle',
  info: 'fas fa-info-circle',
};

const bgColorMap: Record<NotificationType, string> = {
  success: 'bg-success',
  error: 'bg-danger',
  warning: 'bg-warning',
  info: 'bg-info',
};

/**
 * Individual notification toast
 */
export const NotificationToast: React.FC<NotificationToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onDismiss,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  return (
    <div className="toast show mb-2" role="alert" aria-live="assertive" aria-atomic="true">
      <div className={`toast-header ${bgColorMap[type]} text-white`}>
        <i className={`${iconMap[type]} me-2`}></i>
        <strong className="me-auto">{title}</strong>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={() => onDismiss(id)}
          aria-label="Close"
        ></button>
      </div>
      {message && (
        <div className="toast-body">
          {message}
        </div>
      )}
    </div>
  );
};

export default NotificationToast;
